import { useEffect, useMemo, useState } from 'react'
import {
    CommonConstant,
    CommonUtility,
    ErrorConstant,
    InspectionTaskService,
    PaginationType,
    ReportService,
} from 'utility'

export const GetInspectionTaskList = (
    paginationType = PaginationType.default,
) => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: CommonConstant.defaultPageSize,
        SearchText: '',
        Status: 0,
        UserId: '',
        all: paginationType,
    })
    const options = useMemo(
        () =>
            (data || []).map(ele => ({
                value: ele.TemplateId,
                text: ele.TemplateName,
            })),
        [data],
    )

    const groupedList = useMemo(
        () => CommonUtility.taskgroupedByDates(data || []),
        [data],
    )

    useEffect(() => {
        let isMounted = true
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await InspectionTaskService.list(
                    !filter.all ? filter : { ...filter, PageSize: 5000000 },
                )
                if (isMounted) {
                    setData(result.Items || [])
                    setTotalCount(result.TotalCount || 0)
                }
            } catch {
                if (isMounted) {
                    setError(ErrorConstant.default)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            isMounted = false
        }
    }, [filter])

    const pageChanged = page => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    const filterChanged = data => {
        // Reset PageNumber to 1 when filters change (not when pagination changes)
        // Check if filterData contains any filter fields other than PageNumber
        const hasFilterChanges = data.SearchText !== undefined ||
            data.Status !== undefined || data.UserId !== undefined

        setFilter({
            ...filter,
            ...data,
            PageNumber: hasFilterChanges ? 1 : (data.PageNumber || filter.PageNumber),
            PageSize: 20, // Always ensure PageSize is 20
        })
    }

    return {
        data,
        groupedList,
        setData,
        options,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        refresh,
        filterChanged,
    }
}

/** Dashboard/Tasks row: AuditTemplateId first, TemplateId only when that is empty. */
export function resolveResumeInspectionId(task) {
    if (!task) return ''
    const auditTemplateId =
        task.AuditTemplateId ?? task.auditTemplateId ?? task.AuditTemplateID
    if (auditTemplateId != null && String(auditTemplateId).trim() !== '') {
        return String(auditTemplateId).trim()
    }
    const templateId = task.TemplateId ?? task.templateId
    if (templateId == null || String(templateId).trim() === '') return ''
    return String(templateId).trim()
}

export const useScheduledTaskList = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)

    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: CommonConstant.defaultPageSize,
    })

    const options = useMemo(
        () =>
            (data || []).map(ele => ({
                value: ele.TemplateId,
                text: ele.Title,
            })),
        [data],
    )

    useEffect(() => {
        let isMounted = true
        const fetchScheduledTasks = async () => {
            try {
                setLoading(true);
                const result = await InspectionTaskService.getScheduleTask(filter);
                console.log('API Response:', result); // Debug log
                if (isMounted) {
                    setData(result.Tasks || []);
                    setTotalCount(result.TotalCount || 0);
                }
            } catch (error) {
                console.error('Error fetching scheduled tasks:', error);
                if (isMounted) {
                    setError(error?.message || ErrorConstant.default);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchScheduledTasks()

        return () => {
            isMounted = false
        }
    }, [filter])

    const pageChanged = page => {
        console.log('Page changed to:', page); // Debug log
        setFilter(prev => ({
            ...prev,
            PageNumber: page,
        }));
    };

    const refresh = () => {
        setFilter(prev => ({ ...prev }));
    };

    const filterChanged = newFilters => {
        setFilter(prev => ({
            ...prev,
            ...newFilters,
        }));
    }
    return {
        data,
        setData,
        options,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        refresh,
        filterChanged,
    }
}

export const GetDueTemplatesList = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 20,
        total: 0,
        from: 1,
        to: 20,
    })
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: CommonConstant.defaultPageSize,
        SearchText: '',
        UserId: '',
        StartDate: '',
        EndDate: '',
    })

    const options = useMemo(
        () =>
            (data || []).map(ele => ({
                value: ele.TemplateId,
                text: ele.TemplateName,
            })),
        [data],
    )

    const groupedList = useMemo(
        () => CommonUtility.taskgroupedByDates(data || []),
        [data],
    )

    useEffect(() => {
        // Only fetch data if StartDate and EndDate are provided
        if (!filter.StartDate || !filter.EndDate) {
            return undefined
        }

        let isMounted = true
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await ReportService.getCombineTemplateReportPost(filter)
                if (isMounted) {
                    setData(result.Items || [])
                    if (result.MetaData) {
                        setPagination({
                            currentPage: result.MetaData.CurrentPage,
                            lastPage: result.MetaData.LastPage,
                            perPage: result.MetaData.PerPage,
                            total: result.MetaData.Total,
                            from: result.MetaData.From,
                            to: result.MetaData.To,
                        })
                    }
                    setTotalCount(result.TotalCount || 0)
                }
            } catch {
                if (isMounted) {
                    setError(ErrorConstant.default)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            isMounted = false
        }
    }, [filter])

    const pageChanged = page => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    const filterChanged = data => {
        setFilter({
            ...filter,
            ...data,
            PageNumber: 1,
        })
    }

    return {
        data,
        groupedList,
        setData,
        options,
        error,
        loading,
        filter,
        totalCount,
        pagination,
        pageChanged,
        refresh,
        filterChanged,
    }
}
